import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDoc, getDocs, query, orderBy, limit, startAfter, where, startAt, endAt } from 'firebase/firestore';
import { db } from '../../firebase';
import './Ads.css';
import MultiplexAd from "./MultiplexAds";
import { LoadingSkeleton } from "../../components/LoadingSkeleton/LoadingSkeleton";
import { slugify } from '../../utils/helpers';
import { CategoryGrid } from '../../components/CategoryGrid/CategoryGrid';
import { useLocation } from '../../contexts/LocationContext';

// ✅ ADD THIS
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { useAdsConfig } from '../../hooks/useAdsConfig';
import { doc } from "firebase/firestore";
import { geohashQueryBounds, distanceBetween } from "geofire-common";
import { FeaturedRibbon, NewListingRibbon, TrendingTag } from '../../components/AdTags/AdTags';
import PromoBanner from '../../components/Promos/PromoBanner';
import { formatPriceINR } from '../../utils/priceUtils';
import { Close, Refresh } from '@mui/icons-material';
import { FiEye } from 'react-icons/fi';

const algoliaClient = algoliasearch('T9Z9MPIFRV', 'b27cd5b5a6fa953e23729454aa1e741c');


const ADS_PER_PAGE = 20;



// --- THE MAGIC: GLOBAL MEMORY CACHE ---
// Because this is OUTSIDE the component, it does not get destroyed
// when you navigate to the Ad Detail page.
const listCache = {
  ads: [],
  lastVisible: null,
  hasMore: true,
  isLoaded: false,
  locationKey: null,
  scrollPosition: 0
};

const getLocationCacheKey = (location) => {
  if (!location?.latitude || !location?.longitude) return null;

  return [
    location.city || '',
    Number(location.latitude).toFixed(4),
    Number(location.longitude).toFixed(4)
  ].join('|');
};

const AdsList = () => {
  const adsEnabled = useAdsConfig();
  // Initialize states directly from our cache
  const [ads, setAds] = useState(listCache.ads);
  const [lastVisible, setLastVisible] = useState(listCache.lastVisible);
  const [hasMore, setHasMore] = useState(listCache.hasMore);

  // If it's already cached, we don't need to show the initial loading spinner
  const [loadingInitial, setLoadingInitial] = useState(!listCache.isLoaded);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const { location } = useLocation(); // Watches global location context for geofiltering


  const resultsRef = useRef(null);


  // ✅ ADD THIS
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState("");
  const currentLocationKey = getLocationCacheKey(location);
  const isSearchMode = searchMode && submittedQuery.length >= 2;

  const isAdFeatured = (ad) => {
    if (!ad.isFeatured || !ad.featuredUntil) return false;
    // Convert Firestore Timestamp to JS Date for comparison
    const expiry = ad.featuredUntil?.toDate ? ad.featuredUntil.toDate() : new Date(ad.featuredUntil);
    return expiry > new Date();
  };

  const parseSearchQuery = (query) => {
    let text = query.toLowerCase();

    // Example: "iphone in banjarahills"
    if (text.includes(" in ")) {
      const parts = text.split(" in ");
      return {
        searchText: parts[0],
        location: parts[1]
      };
    }

    return {
      searchText: text,
      location: ''
    };
  };

  // Both the Boost Ads sorting fallback (activeAt falling back to createdAt) and the
  // Featured Ads Interleaving (showing 1 featured ad followed by an interval of 4 standard ads) have been fully integrated into below functions.
  //
  // Key Enhancements Made:
  // Boost Integration (getAdTime): Both queries now look for activeAt
  // (the Boost timestamp) first, falling back to createdAt to determine chronological freshness.
  // Interleaved Layout: Instead of stacking all featured ads at the top,
  // Listings are separated into featuredList and standardList, sorted by freshness, and then dynamically interleaved. With FEATURED_INTERVAL = 4, the feed will render 1 Featured ad, then 4 Standard ads, then 1 Featured ad, repeating this sequence to preserve organic feed health.

  const searchAds = async (query) => {
    if (query.length < 2) return;

    setLoadingInitial(true);
    setSearchLoading(true);

    try {
      const { searchText, location } = parseSearchQuery(query);
      const filters = [];

      if (location) {
        filters.push(`location:${location}`);
      }

      const res = await algoliaClient.search({
        requests: [
          {
            indexName: 'listings',
            query: searchText,
            filters: filters.join(' AND '),
          },
        ],
      });

      const hits = res.results?.[0]?.hits || [];
      console.log('Algolia hits:', hits);

      const ids = hits
        .map((item) => {
          if (item.id) return item.id;
          if (item.listingId) return item.listingId;
          if (item.objectID && !item.objectID.includes('/')) return item.objectID;

          const rawPath = item.path || item.objectID || '';
          const parts = rawPath.split('/').filter(Boolean);
          const listingsIndex = parts.indexOf('listings');

          if (listingsIndex >= 0) return parts[listingsIndex + 1];
          return parts.length > 1 ? parts[parts.length - 1] : null;
        })
        .filter(Boolean);

      console.log('Algolia listing ids:', ids);

      const docs = await Promise.all(
        ids.map((id) => getDoc(doc(db, 'listings', id)))
      );

      const fullData = docs
        .filter((snapshot) => snapshot.exists() && snapshot.data().status === 'active')
        .map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }))
        .filter((ad) => ad.adType !== 'test' && ad.adType !== '');

      console.log('Hydrated search ads:', fullData.length);

      // Helper to retrieve the active sorting time (prefers boost timestamp "activeAt")
      const getAdTime = (ad) => {
        const activeTime = ad.activeAt?.toMillis?.() || ad.activeAt?.seconds * 1000 || 0;
        if (activeTime) return activeTime;
        return ad.createdAt?.toMillis?.() || ad.createdAt?.seconds * 1000 || 0;
      };

      // Separate featured and standard ads
      const featuredList = [];
      const standardList = [];

      fullData.forEach((ad) => {
        if (isAdFeatured(ad)) {
          featuredList.push(ad);
        } else {
          standardList.push(ad);
        }
      });

      // Sort both arrays independently by active/boost time descending
      const sortDesc = (a, b) => getAdTime(b) - getAdTime(a);
      featuredList.sort(sortDesc);
      standardList.sort(sortDesc);

      // Interleave featured ads into standard feed
      const interleavedList = [];
      let featuredIndex = 0;
      let standardIndex = 0;
      const FEATURED_INTERVAL = 4; // Yields 1 featured, then 4 standard ads, etc.

      while (standardIndex < standardList.length || featuredIndex < featuredList.length) {
        if (featuredIndex < featuredList.length && (interleavedList.length % (FEATURED_INTERVAL + 1) === 0)) {
          interleavedList.push(featuredList[featuredIndex]);
          featuredIndex++;
        } else if (standardIndex < standardList.length) {
          interleavedList.push(standardList[standardIndex]);
          standardIndex++;
        } else {
          interleavedList.push(featuredList[featuredIndex]);
          featuredIndex++;
        }
      }

      setAds(interleavedList);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
      setHasMore(false);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoadingInitial(false);
      setSearchLoading(false);
    }
  };

  const fetchAds = useCallback(async (isLoadMore = false) => {
    if (!location) return;
    if (searchMode) return;

    if (isLoadMore) setLoadingMore(true);
    else setLoadingInitial(true);

    try {
      const center = [location.latitude, location.longitude];
      const radiusInM = 50 * 1000;

      const bounds = geohashQueryBounds(center, radiusInM);
      const promises = [];

      // 🔥 STEP 1: Fetch all geo-bounded docs
      for (const b of bounds) {
        const q = query(
          collection(db, "listings"),
          where('status', '==', 'active'),
          orderBy("locationData.geoHashData.geohash"),
          startAt(b[0]),
          endAt(b[1])
        );
        promises.push(getDocs(q));
      }

      const snapshots = await Promise.all(promises);

      let allAds = [];

      // 🔥 STEP 2: Merge + filter
      snapshots.forEach((snap) => {
        snap.docs.forEach((doc) => {
          const data = doc.data();

          const lat = data.locationData?.latitude;
          const lng = data.locationData?.longitude;

          if (!lat || !lng) return;

          const distanceInKm = distanceBetween([lat, lng], center);
          const distanceInM = distanceInKm * 1000;

          if (distanceInM <= radiusInM) {
            // ✅ CATEGORY FILTER (manual)
            if (!selectedCategory || data.category === selectedCategory) {
              allAds.push({
                id: doc.id,
                ...data,
                distanceInKm,
              });
            }
          }
        });
      });

      // 🔥 STEP 3: Sort & Interleave (Prepares boost/active timelines and interleaves featured listings)
      const getAdTime = (ad) => {
        const activeTime = ad.activeAt?.toMillis?.() || ad.activeAt?.seconds * 1000 || 0;
        if (activeTime) return activeTime;
        return ad.createdAt?.toMillis?.() || ad.createdAt?.seconds * 1000 || 0;
      };

      const featuredList = [];
      const standardList = [];

      allAds.forEach((ad) => {
        if (isAdFeatured(ad)) {
          featuredList.push(ad);
        } else {
          standardList.push(ad);
        }
      });

      const sortDesc = (a, b) => getAdTime(b) - getAdTime(a);
      featuredList.sort(sortDesc);
      standardList.sort(sortDesc);

      const interleavedList = [];
      let featuredIndex = 0;
      let standardIndex = 0;
      const FEATURED_INTERVAL = 4; // Yields 1 featured, then 4 standard ads, etc.

      while (standardIndex < standardList.length || featuredIndex < featuredList.length) {
        if (featuredIndex < featuredList.length && (interleavedList.length % (FEATURED_INTERVAL + 1) === 0)) {
          interleavedList.push(featuredList[featuredIndex]);
          featuredIndex++;
        } else if (standardIndex < standardList.length) {
          interleavedList.push(standardList[standardIndex]);
          standardIndex++;
        } else {
          interleavedList.push(featuredList[featuredIndex]);
          featuredIndex++;
        }
      }

      allAds = interleavedList;

      // 🔥 STEP 4: Pagination (manual)
      const startIndex = isLoadMore ? ads.length : 0;
      const paginatedAds = allAds.slice(
        startIndex,
        startIndex + ADS_PER_PAGE
      );

      // 🔥 STEP 5: Update state
      setAds((prev) => {
        const updated = isLoadMore ? [...prev, ...paginatedAds] : paginatedAds;

        if (!selectedCategory && !searchMode) {
          listCache.ads = updated;
          listCache.locationKey = currentLocationKey;
        }

        return updated;
      });

      // ❌ Firestore cursor no longer valid → replace with index-based logic
      setLastVisible(startIndex + ADS_PER_PAGE);

      if (!selectedCategory && !searchMode) {
        listCache.lastVisible = startIndex + ADS_PER_PAGE;
      }

      const moreAvailable = allAds.length > startIndex + ADS_PER_PAGE;

      setHasMore(moreAvailable);

      if (!selectedCategory && !searchMode) {
        listCache.hasMore = moreAvailable;
      }

      listCache.isLoaded = true;
      if (!selectedCategory && !searchMode) {
        listCache.locationKey = currentLocationKey;
      }

    } catch (error) {
      console.error("Error fetching geo ads:", error);
    } finally {
      setLoadingInitial(false);
      setLoadingMore(false);
    }
  }, [location, selectedCategory, searchMode, ads.length, currentLocationKey]);

  // --- MOUNT & UNMOUNT LOGIC ---
  useEffect(() => {
    const run = async () => {
      const isSameLocationCache =
        listCache.isLoaded &&
        listCache.locationKey &&
        listCache.locationKey === currentLocationKey;

      if (searchMode && submittedQuery.length >= 2) {
        await searchAds(submittedQuery);
        return;
      }

      // 🔥 CATEGORY MODE
      if (selectedCategory) {
        fetchAds(false);
        return;
      }

      // NORMAL MODE
      if (!isSameLocationCache) {
        fetchAds(false); // 📦 normal Firebase listing
      } else {
        restoreFromCache();
        setTimeout(() => {
          window.scrollTo(0, listCache.scrollPosition);
        }, 50);
      }

    };

    run();

    return () => {
      listCache.scrollPosition = window.scrollY;
    };
  }, [selectedCategory, location, currentLocationKey, searchMode, submittedQuery, fetchAds]);

  const restoreFromCache = () => {
    setAds(listCache.ads);
    setLastVisible(listCache.lastVisible);
    setHasMore(listCache.hasMore);
  };



  const parseDate = (value) => {
    if (!value) return null;

    // ✅ Firestore Timestamp (most important)
    if (typeof value.toDate === "function") {
      return value.toDate();
    }

    // fallback (older structure)
    if (value?.seconds) {
      return new Date(value.seconds * 1000);
    }

    // normal JS Date
    if (value instanceof Date) return value;

    return null;
  };

  const isNew = (createdAt) => {
    const date = parseDate(createdAt);
    if (!date || isNaN(date)) return false;

    const diffDays = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 1;
  };

  const isTrending = (views) => {
    return views > 100; // tweak this
  };


  return (
    <>
      <div className="listings-page">
        {/* <div className="listing-header">
        <div className="container">
          <h1 className="section-title light-text" style={{ color: 'white' }}>Explore Listings</h1>
          <p className="section-subtitle light-text" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Find great deals in your neighborhood.
          </p>
        </div>
      </div> */}

        <div className="container ads-container">
          {loadingInitial ? (
            <LoadingSkeleton type="list" />
          ) : (
            <>
              <CategoryGrid
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={() => {
                  const query = searchQuery.trim();
                  if (query.length < 2) return;

                  setSelectedCategory(null); // Clear category filter when searching
                  setSearchMode(true);
                  setSubmittedQuery(query);
                }}
                onCategorySelect={(category) => {
                  setSelectedCategory(category);
                  setSearchMode(false); // stop search mode
                  setSubmittedQuery("");
                }}
              />
              <div className="listing-header" ref={resultsRef}>
                <div className="container">

                  <div>
                    {selectedCategory ? (
                      <>
                        <h1 className="section-title light-text">
                          Results for "{selectedCategory}"
                        </h1>
                        <p className="section-subtitle light-text">
                          Showing relevant results based on your category selection
                        </p>
                      </>


                    ) : searchMode ? (
                      <>
                        <h1 className="section-title light-text">
                          Results for "{submittedQuery}"
                        </h1>
                        <p className="section-subtitle light-text">
                          Showing relevant results based on your search
                        </p>
                      </>
                    ) : (
                      <>
                        <h1 className="section-title light-text">
                          Recently added listings
                        </h1>
                        <p className="section-subtitle light-text">
                          Discover the latest second-hand deals around you.
                        </p>
                      </>
                    )}
                  </div>

                  {/* ✅ CLEAR SEARCH BUTTON */}
                  {(selectedCategory || searchMode || (searchQuery && searchQuery.length >= 2)) && (
                    <button
                      onClick={() => {
                        setSearchMode(false);
                        setSubmittedQuery("");
                        setSelectedCategory(null);
                        setSearchQuery("");
                        setLastVisible(null);
                        setAds([]);
                        setHasMore(true);
                        if (listCache.isLoaded) {
                          restoreFromCache();
                        } else {
                          fetchAds(false);
                        }
                      }}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #ccc",
                        background: "#fff",
                        cursor: "pointer",
                        borderRadius: "6px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      <Close fontSize="small" style={{ position: 'relative', top: '-1px' }} />
                      <span style={{ lineHeight: 1 }}>Clear Search</span>
                    </button>
                  )}

                </div>
              </div>
              <div className="listing-grid">
                {ads.map((ad) => (
                  <Link to={`/item/${ad.id}/${slugify(ad.title)}`} key={ad.id} className={"listing-card " + (isAdFeatured(ad) ? "featured-premium-card" : "")}>

                    {/* If featured, we add a special glow layer behind everything */}
                    {isAdFeatured(ad) && <div className="premium-glow-layer"></div>}


                    <div className="qinto-card-image-wrapper">
                      <div className="listing-image-container">
                        <div className="ribbon-overlay-container">
                          {/* Badge */}
                          {isNew(ad.createdAt) && (
                            <NewListingRibbon />
                          )}

                          {!isNew(ad.createdAt) && isTrending(ad.viewCount) && (
                            <TrendingTag />
                          )}

                          {isAdFeatured(ad) && (
                            <FeaturedRibbon />
                          )}

                        </div>

                        <img
                          src={
                            Array.isArray(ad.photos) && ad.photos.length > 0
                              ? ad.photos[0]?.thumb || ad.photos[0]
                              : "https://via.placeholder.com/300?text=No+Image"
                          }
                          alt={ad.title}
                          className="listing-image"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300?text=No+Image";
                          }}
                        />
                        <div className="listing-price">{formatPriceINR(ad.price)}</div>
                      </div>
                    </div>

                    <div className="listing-details">
                      <h3 className="listing-title" style={isAdFeatured(ad) ? { color: '#8e24aa' } : {}}>{ad.title}</h3>
                      <div className="listing-meta">
                        <span style={isAdFeatured(ad) ? { color: '#8e24aa' } : {}}>📍 {ad.location || 'Nearby'}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', ...(isAdFeatured(ad) ? { color: '#8e24aa' } : {}) }} title={`${ad.viewCount || 0} views`}>
                          <FiEye size={14} /> {ad.viewCount || 0}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {!isSearchMode && hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '20px' }}>
                  <button
                    className="btn btn-primary-glossy"
                    onClick={() => fetchAds(true)}
                    disabled={loadingMore}
                    style={{ padding: '12px 32px', fontSize: '16px', minWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    {loadingMore ? 'Loading...' : (
                      <>
                        <Refresh fontSize="small" style={{ position: 'relative', top: '-1px' }} />
                        <span style={{ lineHeight: 1 }}>Load More Ads</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {!isSearchMode && !hasMore && ads.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--qinto-text-secondary)' }}>
                  You've seen all the ads! Check back later.
                </div>
              )}

              {isSearchMode && !searchLoading && ads.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--qinto-text-secondary)' }}>
                  No results found for "{submittedQuery}". Try a different search.
                </div>
              )}

              {!isSearchMode && selectedCategory && ads.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--qinto-text-secondary)' }}>
                  No ads found for "{selectedCategory}" in this location yet.
                </div>
              )}

              {!isSearchMode && !selectedCategory && ads.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--qinto-text-secondary)' }}>
                  No ads found in this location yet. Try changing the location or check back later.
                </div>
              )}

              {isSearchMode && !searchLoading && ads.length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--qinto-text-secondary)' }}>
                  That's all we found for "{submittedQuery}". Try a different search.
                </div>
              )}

              {isSearchMode && ads.length > 0 && (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "50px"
                }}>
                  <span style={{ fontSize: "12px", color: "#888" }}>
                    Search powered by
                  </span>
                  <img
                    src="assets/partner/Algolia-logo-blue.png"
                    height="16"
                    alt="Algolia"
                  />
                </div>
              )}


            </>
          )}
        </div>
      </div>
      {/* <img
        src={'/assets/qinto_download_banner_1920.png'}
        alt="Download Banner"
        style={{ marginTop: '30px', marginBottom: '35px' }}
      /> */}

      <div style={{ marginTop: '30px', marginBottom: '35px' }}>
        <PromoBanner />
      </div>

      {adsEnabled && (
        <div className="container">
          <div className="bottom-placement" style={{ marginTop: '40px', marginBottom: '40px' }}>
            <div className="listing-label" style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginBottom: '10px' }}>
              Advertisement
            </div>
            <MultiplexAd />
          </div>
        </div>
      )}

    </>
  );
};

export default AdsList;
